/* eslint-disable max-classes-per-file */
import {Pipe, PipeTransform} from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {ErrorService} from 'webcommon/common-api';
import { Destroyable$, throwAsyncError } from 'webcommon/shared';


// todo: Probably want to delete all this file, and replace usages with EventComposite instead
export enum TrackLoadingEventTypes {
  Loading,
  LoadedValue,
  Error,
}

export type LoadingStatusResult<TResult> =
  CurrentlyLoading |
  LoadedValue<TResult> |
  LoadingFailure;

export interface CurrentlyLoading {
  readonly eventType: TrackLoadingEventTypes.Loading;
}

export interface LoadedValue<TResult> {
  readonly eventType: TrackLoadingEventTypes.LoadedValue;
  readonly value: TResult;
}

export interface LoadingFailure<TError = unknown> {
  readonly eventType: TrackLoadingEventTypes.Error;
  readonly error: TError;
}

export function isCurrentlyLoading<T>(value: LoadingStatusResult<T>): value is CurrentlyLoading {
  return value.eventType === TrackLoadingEventTypes.Loading;
}

export function isLoadedValue<T>(value: LoadingStatusResult<T>): value is LoadedValue<T> {
  return value.eventType === TrackLoadingEventTypes.LoadedValue;
}

export function isLoadingFailure<T>(value: LoadingStatusResult<T>): value is LoadingFailure {
  return value.eventType === TrackLoadingEventTypes.Error;
}

export function getLoadedValueOrNull<T>(value: LoadingStatusResult<T>): LoadedValue<T> | null {
  return isLoadedValue(value) ? value : null;
}

export function trackLoading<T>(
  inputFactory$: () => Observable<T>,
): Observable<LoadingStatusResult<T>> {
  return new Observable<LoadingStatusResult<T>>((subscriber) => {
    subscriber.next({
      eventType: TrackLoadingEventTypes.Loading,
    });

    return inputFactory$().pipe(
      map((val): LoadedValue<T> => {
        return {
          eventType: TrackLoadingEventTypes.LoadedValue,
          value: val,
        };
      }),
      catchError((error: unknown) => {
        throwAsyncError(error);
        return of<LoadingFailure>({
          eventType: TrackLoadingEventTypes.Error,
          error,
        });
      }),
    ).subscribe(subscriber);
  });
}

// this can be used in replacement of switchMap,
// to wrap a specific part of a pipe in trackLoading()
export function trackLoadingSwitchMap<TInput, TResult>(
  inputFactory$: (value: TInput) => Observable<TResult>,
) {
  return switchMap<TInput, Observable<LoadingStatusResult<TResult>>>((value) => {
    return trackLoading(() => {
      return inputFactory$(value);
    });
  });
}

// this can be used in replacement of map to only map the loaded value
// normally this would be used after trackLoadingSwitchMap in the pipe
export function trackLoadingMap<TSource, TTarget>(
  mapFn: (value: TSource) => TTarget,
) {
  return map<LoadingStatusResult<TSource>, LoadingStatusResult<TTarget>>((modalResult) => {
    return convertLoadingStatusResult(modalResult, mapFn);
  });
}

export function convertLoadingStatusResult<TSource, TTarget>(
  source: LoadingStatusResult<TSource>,
  mapFn: (value: TSource) => TTarget,
): LoadingStatusResult<TTarget> {
  if (!isLoadedValue(source)) {
    return source;
  }

  return {
    eventType: TrackLoadingEventTypes.LoadedValue,
    value: mapFn(source.value),
  } satisfies LoadedValue<TTarget>;
}

export interface TrackLoadingReturn<TResult> {
  readonly values$: Observable<LoadingStatusResult<TResult>>;
  tryReload(): void;
}

// better version of trackLoading, which allows triggering a reload,
// even after there has been an error in the load
export function trackLoading2<T>(
  destroyable: Destroyable$,
  inputFactory$: () => Observable<T>,
): TrackLoadingReturn<T> {
  const _load$ = new ReplaySubject<void>(1);
  destroyable.onDestroy$.subscribe(() => {
    _load$.complete();
  });
  const _values$ = trackLoading(() => {
    return _load$.pipe(
      switchMap(inputFactory$),
    );
  });
  return {
    values$: _values$,
    tryReload: () => {
      _load$.next();
    },
  };
}

@Pipe({
  name: 'toCurrentlyLoading',
})
export class ToCurrentlyLoadingPipe implements PipeTransform {
  transform<T>(value: LoadingStatusResult<T>): CurrentlyLoading | null {
    return isCurrentlyLoading(value) ? value : null;
  }
}

@Pipe({
  name: 'toLoadedValue',
})
export class ToLoadedValuePipe implements PipeTransform {
  transform<T>(value: LoadingStatusResult<T>): LoadedValue<T> | null {
    return getLoadedValueOrNull(value);
  }
}

@Pipe({
  name: 'toLoadingFailure',
})
export class ToLoadingFailurePipe implements PipeTransform {
  transform<T>(value: LoadingStatusResult<T>): LoadingFailure | null {
    return isLoadingFailure(value) ? value : null;
  }
}

@Pipe({
  name: 'toErrorMessages',
})
export class ToErrorMessagesPipe implements PipeTransform {
  constructor(
    private errorService: ErrorService,
  ) {
  }

  transform<T>(value: LoadingStatusResult<T>): string[] | null {
    return isLoadingFailure(value) ? this.errorService.getErrors(value.error) : null;
  }
}
